/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import {
    AuthError,
    makeGetRequest,
    makeOptionsRequest,
    makePostRequest,
    makePutRequest
} from "../../network/http-client"
import type { ResetPassword, UserChangePassword } from "../user/user-types"

import type { SessionStore } from "./session-store"
import type { CurrentUser, CurrentUserPreferences, Login, LoginFlowResult } from "./session-types"
import { LoginStatus } from "./session-types"

enum Endpoint {
    login = "/login",
    logout = "/logout",
    currentUser = "/api/private/user/me",
    user = "/api/private/user",
    resetPassword = "/api/public/user/reset-password",
    csrf = "/csrf"
}

export class SessionService {
    constructor(
        private readonly sessionStore: SessionStore,
        private readonly logger: LoggerStore
    ) {}

    async login(username: string, password: string, totp: string | null): Promise<LoginFlowResult> {
        const loginData: Login = {
            username,
            password,
            code: totp ?? ""
        }

        const login = await makePostRequest<URLSearchParams, string>(`${Endpoint.login}`, {}, toUrlEncoded(loginData))

        if (login.success) {
            return { status: LoginStatus.Success }
        }

        if (login.error instanceof Error) {
            this.logger.logError(login.error)
            switch (login.error.message) {
                case AuthError.TOTP_REQUIRED:
                    return { status: LoginStatus.TotpRequired }

                case AuthError.INVALID_CREDENTIALS:
                    return {
                        status: LoginStatus.Error,
                        message: "Invalid credentials"
                    }

                default:
                    return {
                        status: LoginStatus.Error,
                        message: "Login error"
                    }
            }
        }
        // Fallback
        this.logger.logApiError("Login failed", login.apiError, login.error)
        return {
            status: LoginStatus.Error,
            message: "Login failed"
        }
    }

    async logout(): Promise<void> {
        const logout = await makePostRequest<void, void>(`${Endpoint.logout}`, {})

        if (logout.success) {
            this.sessionStore.removeCurrentUser()
        }
        if (!logout.success) {
            this.logger.logApiError("Logout failed", logout.apiError, logout.error)
        }
    }

    // XSRF token fetch
    async fetchXSRFToken(): Promise<void> {
        const response = await makeOptionsRequest<void>(`${Endpoint.csrf}`, {})

        if (!response.success) {
            this.logger.logErrorMessage("Error fetching XSRF token")
            return
        }
    }

    async getCurrentUser(suppressErrorLogging: boolean): Promise<Result<CurrentUser>> {
        const currentUser = await makeGetRequest<CurrentUser>(`${Endpoint.currentUser}`, {})

        if (currentUser.success && currentUser.data) {
            this.sessionStore.setCurrentUser(currentUser.data)
        }

        if (!currentUser.success && !suppressErrorLogging) {
            this.logger.logApiError("Get current user failed", currentUser.apiError, currentUser.error)
        }

        return currentUser
    }

    async updateCurrentUser(preferences: CurrentUserPreferences): Promise<Result<void>> {
        const updated = await makePutRequest<{ preferences: CurrentUserPreferences }, void>(
            `${Endpoint.currentUser}`,
            {},
            { preferences: preferences }
        )

        if (updated.success) {
            this.logger.logOk("User updated successfully")
        }
        if (!updated.success) {
            this.logger.logApiError("Update current user failed", updated.apiError, updated.error)
        }
        return updated
    }

    async changeUserPassword(userId: number, oldPassword: string, newPassword: string): Promise<Result<void>> {
        const res = await makePostRequest<UserChangePassword, void>(
            `${Endpoint.user}/${userId}/change-password`,
            {},
            { oldPassword, newPassword }
        )
        if (res.success) {
            this.logger.logOk("Password chanted successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Change user password failed", res.apiError, res.error)
        }
        return res
    }

    async resetPassword(token: string, password: string): Promise<Result<void>> {
        const res = await makePostRequest<ResetPassword, void>(`${Endpoint.resetPassword}`, {}, { token, password })
        if (res.success) {
            this.logger.logOk("Password reset successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Password reset failed", res.apiError, res.error)
        }
        return res
    }
}

function toUrlEncoded(data: Login): URLSearchParams {
    const params = new URLSearchParams()
    params.append("username", data.username)
    params.append("password", data.password)
    params.append("code", data.code ?? "")
    return params
}
